"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Modal, ModalContent, ModalHeader } from "@/components/ui/modal";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import {
  getPermissionErrorMessage,
  isPermissionDenied,
} from "@/lib/permissions";
import { ApiError } from "@/lib/api/error";
import type { PipelineStage } from "@/lib/api/types";
import { StageInput } from "@/features/jobs/components/stage-input";

import {
  resolveImportStageId,
  toImportStages,
} from "@/features/jobs/components/profile-import/import-stage";

import { uploadImportFile } from "./upload-import-file";

const ACCEPT_ATTR = ".pdf,.docx,.rtf,.txt,.png,.jpeg,.jpg,.webp";
const ACCEPTED_EXTS = [
  ".pdf",
  ".docx",
  ".rtf",
  ".txt",
  ".png",
  ".jpeg",
  ".jpg",
  ".webp",
];
const MAX_FILE_MB = 10;
// Per-batch cap in the modal. Matches the org-level concurrent-imports
// ceiling (`PROFILE_IMPORT_MAX_ACTIVE_PER_ORG=500`) so a single drop can
// saturate the backend cap without the modal pre-rejecting files —
// overflow past 500 will fail cleanly with IMPORT_QUEUE_FULL if the org
// already has active imports.
const MAX_FILES = 500;

type FileItemState = "uploading" | "uploaded" | "error";

interface FileItem {
  id: string;
  file: File;
  state: FileItemState;
  progress: number; // 0-100
  error?: string;
  controller?: AbortController;
}

interface ProfileImportUploadModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  stages: PipelineStage[];
  /**
   * Preferred stage to default to when the modal opens (typically the
   * stage currently selected in the pipeline view). If omitted, not a
   * valid import target (e.g. Archived), or not found in `stages`,
   * falls back to the first importable stage by position.
   */
  initialStageId?: string | null;
  onUploaded: () => void;
}

export function ProfileImportUploadModal({
  open,
  onClose,
  jobId,
  stages,
  initialStageId,
  onUploaded,
}: ProfileImportUploadModalProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const importStages = useMemo(() => toImportStages(stages), [stages]);
  const [stageId, setStageId] = useState<string>(() =>
    resolveImportStageId(toImportStages(stages), initialStageId),
  );
  const [items, setItems] = useState<FileItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  // Bump on every successful upload so the parent can re-invalidate progress.
  const notifyParent = useCallback(() => {
    onUploaded();
  }, [onUploaded]);

  // Reset state whenever the modal is reopened. Abort any in-flight uploads
  // from a previous open so we don't leak network requests.
  useEffect(() => {
    if (open) {
      setItems([]);
      setErrorBanner(null);
      setStageId(resolveImportStageId(importStages, initialStageId));
    } else {
      setItems((prev) => {
        for (const item of prev) item.controller?.abort();
        return prev;
      });
    }
  }, [open, importStages, initialStageId]);

  const updateItem = useCallback(
    (id: string, patch: Partial<FileItem>) =>
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      ),
    [],
  );

  const startUpload = useCallback(
    (item: FileItem, targetStageId: string) => {
      const controller = new AbortController();
      updateItem(item.id, { controller, state: "uploading", progress: 0 });

      uploadImportFile({
        jobId,
        stageId: targetStageId,
        file: item.file,
        signal: controller.signal,
        onProgress: (pct) => updateItem(item.id, { progress: pct }),
      })
        .then((resp) => {
          const rejected = resp?.rejected ?? [];
          if (rejected.length > 0) {
            // Server accepted the HTTP call but rejected the file (unsupported
            // type, magic-number mismatch, too large, etc.). Surface per-file.
            updateItem(item.id, {
              state: "error",
              progress: 0,
              error: rejected[0]?.reason || "Rejected by server",
            });
            return;
          }
          updateItem(item.id, { state: "uploaded", progress: 100 });
          notifyParent();
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") {
            return; // user closed the modal / removed the file
          }
          if (err instanceof ApiError && isPermissionDenied(err)) {
            updateItem(item.id, {
              state: "error",
              progress: 0,
              error: getPermissionErrorMessage(err),
            });
            return;
          }
          const message = err instanceof Error ? err.message : "Upload failed";
          updateItem(item.id, {
            state: "error",
            progress: 0,
            error: message,
          });
        });
    },
    [jobId, notifyParent, updateItem],
  );

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      // Defense in depth: UI also gates via `canUpload`, but never start
      // uploads with an empty stageId (empty / no-importable-stages edge case).
      if (!stageId) return;

      setErrorBanner(null);
      const list = Array.from(incoming);
      const rejected: string[] = [];
      const newItems: FileItem[] = [];
      for (const file of list) {
        const reason = validateFile(file);
        if (reason) {
          rejected.push(`${file.name}: ${reason}`);
          continue;
        }
        newItems.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
            .toString(36)
            .slice(2, 8)}`,
          file,
          state: "uploading",
          progress: 0,
        });
      }

      // Compute what we'll actually accept WITHOUT touching a state updater,
      // so the side effect of kicking off XHR uploads only fires once.
      // (React Strict Mode intentionally invokes `setState` updaters twice
      // in dev to surface impurity — which would fire the upload twice per
      // file and produce two `ProfileImport` rows per file.)
      setItems((prev) => {
        const capacity = Math.max(0, MAX_FILES - prev.length);
        const accepted = newItems.slice(0, capacity);
        return [...prev, ...accepted];
      });

      const { accepted, overflow } = (() => {
        // We don't know `prev.length` here synchronously, so mirror the
        // slice logic against the current `items` snapshot. The worst case
        // is that we kick off one extra upload that the server rejects via
        // the org cap; Strict Mode stays safe because this runs exactly
        // once per `addFiles` call.
        const capacity = Math.max(0, MAX_FILES - items.length);
        return {
          accepted: newItems.slice(0, capacity),
          overflow: newItems.length > capacity,
        };
      })();

      if (overflow) {
        rejected.push(`You can upload at most ${MAX_FILES} files at a time.`);
      }
      if (rejected.length) setErrorBanner(rejected.join("; "));

      if (accepted.length > 0) {
        const currentStage = stageId;
        for (const item of accepted) startUpload(item, currentStage);
      }
    },
    [items.length, stageId, startUpload],
  );

  const handleBrowseFile = () => fileInput.current?.click();
  const handleBrowseFolder = () => folderInput.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!stageId) return;
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const remove = useCallback((id: string) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item?.state === "uploading") item.controller?.abort();
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const counts = useMemo(() => {
    const uploading = items.filter((i) => i.state === "uploading").length;
    const uploaded = items.filter((i) => i.state === "uploaded").length;
    const failed = items.filter((i) => i.state === "error").length;
    return { uploading, uploaded, failed, total: items.length };
  }, [items]);

  const anyUploading = counts.uploading > 0;
  // Mirror wizard `canUpload`: no importable stage → block dropzone / addFiles
  // so we never POST with `stageId: ""`. Mid-batch stays open when a valid
  // stage is already selected (`stageId` truthy).
  const canUpload = Boolean(stageId);
  const canClose = !anyUploading;

  const progressLabel = anyUploading
    ? `Uploading ${counts.uploaded + counts.failed} / ${counts.total}`
    : counts.total > 0
      ? `${counts.uploaded} uploaded${counts.failed ? ` · ${counts.failed} failed` : ""}`
      : null;

  // When the last upload lands, show a small success toast (only once per
  // batch — gated by `anyUploading` transitioning to false while total > 0).
  const previousUploading = useRef(false);
  useEffect(() => {
    if (previousUploading.current && !anyUploading && counts.uploaded > 0) {
      showToast.success(
        `Queued ${counts.uploaded} profile${
          counts.uploaded === 1 ? "" : "s"
        } for import`,
      );
    }
    previousUploading.current = anyUploading;
  }, [anyUploading, counts.uploaded]);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (!o ? canClose && onClose() : undefined)}
    >
      <ModalContent size="lg" className="max-h-[92dvh] sm:max-w-[730px]">
        <ModalHeader title="Import Profiles" />

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-6">
          {/* Stage selector */}
          <section className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-heading leading-5">
              Choose the stage to add these imported profiles to
            </label>
            <p className="text-sm text-contrast/60 leading-normal">
              All imported profiles will be added to the below selected stage.
            </p>
            <StageInput
              jobId={jobId}
              value={stageId}
              onChange={(stage) => setStageId(stage.id)}
              excludeArchived
              disabled={anyUploading || !canUpload}
              className="mt-1"
            />
          </section>

          {/* Drop zone */}
          <div
            onDragOver={(e) => {
              if (!canUpload) return;
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => canUpload && handleBrowseFile()}
            className={
              "flex flex-col items-center gap-4 rounded-lg border border-dashed px-4 py-12 text-center transition-colors " +
              (canUpload ? "cursor-pointer " : "cursor-not-allowed opacity-60 ") +
              (dragOver
                ? "border-primary bg-primary/5"
                : "border-contrast/15 hover:border-contrast/25") +
              (!canUpload ? " pointer-events-none" : "")
            }
          >
            <Icon
              name="IconCloudUpload"
              size={32}
              className="text-heading opacity-40"
            />
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                disabled={!canUpload}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canUpload) return;
                  handleBrowseFile();
                }}
                className="flex h-7 cursor-pointer items-center gap-1 rounded-md border border-contrast/15 bg-contrast/4 px-2 text-sm font-medium text-heading shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)] hover:bg-contrast/8 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Icon name="IconArrowOutOfBox" size={14} />
                Upload File
              </button>
              <button
                type="button"
                disabled={!canUpload}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canUpload) return;
                  handleBrowseFolder();
                }}
                className="flex h-7 cursor-pointer items-center gap-1 rounded-md border border-contrast/15 bg-contrast/4 px-2 text-sm font-medium text-heading shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)] hover:bg-contrast/8 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Icon name="IconFolderUpload" size={14} />
                Upload Folder
              </button>
            </div>
            <p className="text-sm text-contrast/60">
              Uploads start automatically as soon as you add files
            </p>
            <div className="rounded-md bg-contrast/4 px-3 py-1">
              <span className="text-xs text-contrast/60">
                PDF / DOCX / RTF / TXT / PNG / JPEG / WEBP up to {MAX_FILE_MB}MB
              </span>
            </div>
          </div>

          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            disabled={!canUpload}
            onChange={handleChange}
            className="hidden"
          />
          <input
            ref={folderInput}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            disabled={!canUpload}
            /* @ts-expect-error webkitdirectory is non-standard but widely supported */
            webkitdirectory=""
            onChange={handleChange}
            className="hidden"
          />

          {/* Progress pill */}
          {progressLabel && (
            <div className="flex items-center justify-end">
              <div className="flex h-7 items-center gap-2 rounded-md border border-contrast/5 bg-contrast/3 px-2">
                <Icon
                  name={anyUploading ? "IconLoadingCircle" : "IconCircleCheck"}
                  size={14}
                  className={
                    anyUploading
                      ? "animate-spin text-heading opacity-70"
                      : "text-[#15803d] dark:text-[#66dc7e] opacity-80"
                  }
                />
                <span className="text-sm font-medium text-heading">
                  {progressLabel}
                </span>
              </div>
            </div>
          )}

          {/* File list */}
          {items.length > 0 && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {items.map((item) => (
                <FileChip
                  key={item.id}
                  item={item}
                  onRemove={() => remove(item.id)}
                />
              ))}
            </div>
          )}

          {/* Error banner */}
          {errorBanner && (
            <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive dark:border-[rgba(220,102,104,0.2)] dark:bg-[rgba(220,102,104,0.08)] dark:text-[#ff888a]">
              {errorBanner}
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
}

function FileChip({
  item,
  onRemove,
}: {
  item: FileItem;
  onRemove: () => void;
}) {
  const sizeLabel = formatBytes(item.file.size);
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-contrast/8 bg-contrast/3 px-2.5 py-2">
      <div className="flex items-start gap-2">
        <Icon
          name="IconFileBend"
          size={16}
          className="shrink-0 text-heading opacity-70"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-heading">
            {item.file.name}
          </div>
          <div className="flex items-center gap-1 text-xs text-contrast/50">
            <span>{sizeLabel}</span>
            <span>·</span>
            {item.state === "uploaded" ? (
              <span className="text-[#15803d] dark:text-[#66dc7e]">
                Uploaded
              </span>
            ) : item.state === "error" ? (
              <span className="text-destructive dark:text-[#ff888a]">{item.error || "Failed"}</span>
            ) : item.progress >= 100 ? (
              // Bytes are fully sent but the server is still sanitizing /
              // scheduling the workflow. Be honest about that phase rather
              // than parking at "99% uploading" which reads as stuck.
              <span className="text-heading/80">Finalizing…</span>
            ) : (
              <span className="text-heading/80">Uploading…</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${item.file.name}`}
          className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-contrast/40 transition-colors hover:bg-contrast/6 hover:text-contrast/70"
        >
          <Icon name="IconCrossMedium" size={12} />
        </button>
      </div>

      {/* Per-file progress bar — shows while uploading; greens out when done. */}
      <div className="h-1 w-full overflow-hidden rounded-full bg-contrast/10">
        <div
          className={
            "h-full rounded-full transition-[width,background-color] duration-200 " +
            (item.state === "uploaded"
              ? "bg-[#15803d] dark:bg-[#66dc7e]"
              : item.state === "error"
                ? "bg-destructive dark:bg-[#ff888a]"
                : "bg-cta")
          }
          style={{
            width: `${
              item.state === "uploaded"
                ? 100
                : item.state === "error"
                  ? 100
                  : item.progress
            }%`,
          }}
        />
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

function validateFile(file: File): string | null {
  const ext = "." + file.name.split(".").pop()?.toLowerCase();
  if (!ACCEPTED_EXTS.includes(ext)) {
    return `Unsupported type (${ext || "no extension"})`;
  }
  if (file.size === 0) return "File is empty";
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    return `Exceeds ${MAX_FILE_MB}MB`;
  }
  return null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
